import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./jobsV2.scss";
import { Alert, Snackbar } from "@mui/material";
import ApplyStageModal from "../../utils/applyStageModal";
import UploadCandidateModal from "../../utils/UploadCandidateModal";
import ResumeUploadModal from "../../utils/ResumeUploadModal";
import ScoreCircle from "../../utils/ScoreCircle";
import CandidateFilterBody from "../../utils/CandidateFilterBody";
import useCandidatesV2 from "../../utils/useCandidatesV2";
import { JOB_STAGES, SORT_ROWS, getStatusVisual } from "../../utils/candidatesV2Constants";

const CandidatesV2 = () => {
  const {
    jobId,
    searchTerm, selectedJobs,
    file, setFile,
    snackOpen, snackSeverity, snackMessage,
    uploading, loading, exportLoading,
    uploadProgress, assignProgress,
    editModal, setEditModal,
    editForm, setEditForm,
    resumeModal, setResumeModal,
    showUploadModal,
    isOpen, setIsOpen,
    sortField, sortDirection, sortOptions,
    skillsMatchMode, handleSkillsModeChange,
    showExportDropdown, setShowExportDropdown,
    pageSize, setPageSize,
    currentPage, setCurrentPage,
    filterItems, openAccordions, subitemsData, loadingAccordion,
    loadingMoreMap,
    filterValues, filterConfigLoaded,
    filtersChanged,
    saveSearchModal, setSaveSearchModal,
    savedSearchesModal, setSavedSearchesModal,
    csvInputRef, pdfInputRef, resumeInputRef,
    exportDropdownRef, exportDropdownPortalRef,
    candidateList, pagination, totalPages, hiringStatuses,
    currentJobs, isDisabled, activeFilterChips, chipCountByKey,
    handleSearch, handleSortChange, triggerFetch, toggleAccordion, toggleAllAccordions,
    handleCheckbox, handlePill, handleRange, handleStageScore, handleStageStatus, handleResponse,
    handleApplyFilters, handleClearAllFilters, removeChip,
    handleSubitemSearch, handleSubitemLoadMore,
    handleExport,
    handleOpenSaveSearch, handleConfirmSaveSearch,
    handleOpenSavedSearches, handleLoadSavedSearch, handleDeleteSavedSearch,
    handleCsvUpload, handlePdfUpload, resetUploadModal, handleCandidateUpload, downloadSampleCsv,
    handleResumeUpload, handleEditCandidate,
    handleSelectJob, handleSelectAll,
    showSnack, snackClosed,
    handleStatusChange, handleBulkStatusChange,
    statusLoading,
    openApplyStageModal, selectAssignedStage,
    getCandidateStageScore, getDaysFromToday,
  } = useCandidatesV2();

  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortDropdownRef = useRef(null);
  const sortPortalRef = useRef(null);
  const [sortBtnPos, setSortBtnPos] = useState(null);
  const [exportBtnPos, setExportBtnPos] = useState(null);
  const [pendingField, setPendingField] = useState(sortField);
  const [pendingDir, setPendingDir] = useState(sortDirection);

  const enabledFilterKeys = filterItems.filter((i) => i.enabled !== false).map((i) => i.key);
  const isAllAccordionsOpen = enabledFilterKeys.length > 0 && enabledFilterKeys.every((k) => openAccordions.has(k));

  const calcPos = (btnEl, gap, minWidth) => {
    const rect = btnEl.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - minWidth - 8));
    return { top: rect.bottom + gap, left };
  };

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".status-dropdown-v2")) setOpenStatusDropdown(null);
      if (
        sortDropdownRef.current && !sortDropdownRef.current.contains(e.target) &&
        (!sortPortalRef.current || !sortPortalRef.current.contains(e.target))
      ) setShowSortDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (showSortDropdown) {
      setPendingField(sortField);
      setPendingDir(sortDirection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSortDropdown]);

  const renderStageStatusIcon = (status) => {
    if (status === "Expired" || status === "Failed") return (
      <span className="stage-cell-status" title={status}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FF4B4B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </span>
    );
    if (status === "Scheduled") return (
      <span className="stage-cell-status" title={status}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2F80ED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </span>
    );
    if (status === "Completed") return (
      <span className="stage-cell-status" title={status}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4CC948" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      </span>
    );
    return (
      <span className="stage-cell-status" title={status}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FF9500" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
      </span>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    for (let i = start; i <= end; i++) {
      pages.push(
        <button key={i} className={`page-btn ${currentPage === i ? "active" : ""}`}
          onClick={() => setCurrentPage(i)} disabled={isDisabled}>{i}</button>
      );
    }
    return (
      <>
        <button className="page-btn" onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1 || isDisabled}>‹</button>
        {start > 1 && <span className="page-ellipsis">...</span>}
        {pages}
        {end < totalPages && <span className="page-ellipsis">...</span>}
        <button className="page-btn" onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages || isDisabled}>›</button>
      </>
    );
  };

  return (
    <div className="candidates-v2-page">

      {/* ── Sidebar ── */}
      <aside className="candidates-v2-sidebar">
        <div className="sidebar-header-v2">
          {/* Title row + Apply button */}
          <div className="sidebar-header-top-row">
            <div className="sidebar-title-v2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#525B66" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="11" y1="18" x2="13" y2="18" />
              </svg>
              <span>Advanced Search</span>
            </div>
            <button
              className={`apply-filters-btn-v2${filtersChanged ? " active" : ""}`}
              onClick={handleApplyFilters}
              disabled={!filtersChanged || isDisabled}
            >
              Apply
            </button>
          </div>

          {/* Save search / Saved searches row */}
          <div className="sidebar-search-links-row">
            <button className="sidebar-link-btn-v2" onClick={handleOpenSaveSearch} disabled={isDisabled}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
              </svg>
              Save search
            </button>
            <button className="sidebar-link-btn-v2" onClick={handleOpenSavedSearches} disabled={isDisabled}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
              Saved
            </button>
            {/* <button
              type="button"
              className="sidebar-toggle-all-btn-v2"
              onClick={toggleAllAccordions}
              disabled={isDisabled}
              title={isAllAccordionsOpen ? "Collapse all" : "Expand all"}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#525B66" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className={`acc-chevron ${isAllAccordionsOpen ? "open" : ""}`}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button> */}
          </div>
        </div>

        <div className="sidebar-accordion-v2">
          {filterItems.length === 0 && !filterConfigLoaded && (
            <div className="acc-loading-msg">Loading filters…</div>
          )}
          {filterItems.map((item) => {
            const isItemOpen = openAccordions.has(item.key);
            const isSearch = item.key === "search";
            const countBadge = isSearch ? (searchTerm ? 1 : 0) : (chipCountByKey[item.key] || 0);
            const isLoading = loadingAccordion[item.key];
            return (
              <div key={item.key} className={`acc-item-v2 ${isItemOpen ? "open" : ""} ${!item.enabled ? "acc-disabled" : ""}`}>
                <button
                  type="button"
                  className="acc-btn-v2"
                  onClick={() => toggleAccordion(item.key, item.enabled !== false)}
                  disabled={item.enabled === false}
                >
                  <span className="acc-btn-left">
                    <span>{item.label}</span>
                    {item.badge && <span className="acc-soon-badge">{item.badge}</span>}
                    {countBadge > 0 && <span className="filter-badge">{countBadge}</span>}
                  </span>
                  {item.key === "skills" && item.enabled !== false && (
                    <span className="skills-match-toggle-v2" onClick={(e) => e.stopPropagation()}>
                      <span role="button" tabIndex={0} className={`skills-mode-btn-v2${skillsMatchMode === "all" ? " active" : ""}`} onClick={() => handleSkillsModeChange("all")}>AND</span>
                      <span role="button" tabIndex={0} className={`skills-mode-btn-v2${skillsMatchMode === "any" ? " active" : ""}`} onClick={() => handleSkillsModeChange("any")}>OR</span>
                    </span>
                  )}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#525B66" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className={`acc-chevron ${isItemOpen ? "open" : ""}`}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {isItemOpen && (
                  <div className="acc-body-v2">
                    {isSearch ? (
                      <div className="sidebar-search-v2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8A9199" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input type="text" placeholder="Search name or phone..." value={searchTerm} onChange={handleSearch} />
                      </div>
                    ) : isLoading ? (
                      <div className="filter-acc-loading">
                        <div className="spinner-border" style={{ width: "1rem", height: "1rem", color: "#F75891" }} />
                      </div>
                    ) : (
                      <CandidateFilterBody
                        filterKey={item.key}
                        data={subitemsData[item.key]}
                        filterValues={filterValues}
                        onCheckbox={handleCheckbox}
                        onPill={handlePill}
                        onRange={handleRange}
                        onStageScore={handleStageScore}
                        onStageStatus={handleStageStatus}
                        onResponse={handleResponse}
                        onSearch={handleSubitemSearch}
                        onLoadMore={handleSubitemLoadMore}
                        loadingMore={!!loadingMoreMap[item.key]}
                        skillsMatchMode={skillsMatchMode}
                        onSkillsModeChange={handleSkillsModeChange}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="candidates-v2-main">

        {/* Header row */}
        <div className="candidates-v2-header">
          <div className="candidates-header-top-row">
            <div className="candidates-header-left">
              {(activeFilterChips.length > 0 || searchTerm || sortField !== "created_at" || sortDirection !== "desc") ? (
                <span className="candidates-match-count">
                  <strong>{pagination.total || 0}</strong> candidates match your filters
                </span>
              ) : (
                <div />
              )}
              {(sortField !== "created_at" || sortDirection !== "desc") && (() => {
                const currentSortLabel = sortOptions.length > 0
                  ? sortOptions.find((opt) => {
                      const val = opt.sort_hiring_stage_id ? `${opt.key}:${opt.sort_hiring_stage_id}` : opt.key;
                      return val === sortField;
                    })?.label
                  : SORT_ROWS.find((r) => r.field === sortField)?.label;
                return currentSortLabel ? (
                  <span className="sort-indicator-v2">
                    Sorted by: <strong>{currentSortLabel}</strong>
                    <span className="sort-dir-indicator">{sortDirection === "desc" ? "↓" : "↑"}</span>
                  </span>
                ) : null;
              })()}
            </div>

            <div className="candidates-header-actions">
              {/* Sort */}
              <div ref={sortDropdownRef}>
                <button className="header-action-btn-v2" disabled={isDisabled} onClick={() => {
                  if (!showSortDropdown && sortDropdownRef.current)
                    setSortBtnPos(calcPos(sortDropdownRef.current, 6, 260));
                  setShowSortDropdown((s) => !s);
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" />
                  </svg>
                  Sort
                </button>
              </div>
              {showSortDropdown && sortBtnPos && createPortal(
                <div className="sort-dropdown-v2" ref={sortPortalRef}
                  style={{ position: "fixed", top: sortBtnPos.top, left: sortBtnPos.left, zIndex: 9999 }}>
                  <div className="sort-dropdown-head-v2">
                    <span className="sort-dropdown-title-v2">SORT</span>
                    <button type="button" className="sort-reset-btn-v2" onClick={() => { setPendingField("created_at"); setPendingDir("desc"); }}>Reset</button>
                  </div>
                  <div className="sort-section-v2">
                    <span className="sort-section-label-v2">SORT BY</span>
                    <select className="sort-select-v2" value={pendingField} onChange={(e) => setPendingField(e.target.value)}>
                      {sortOptions.length > 0
                        ? sortOptions.map((opt) => {
                            const val = opt.sort_hiring_stage_id ? `${opt.key}:${opt.sort_hiring_stage_id}` : opt.key;
                            return <option key={val} value={val}>{opt.label}</option>;
                          })
                        : SORT_ROWS.map((row) => (
                            <option key={row.field} value={row.field}>{row.label}</option>
                          ))
                      }
                    </select>
                  </div>
                  <div className="sort-section-v2">
                    <span className="sort-section-label-v2">DIRECTION</span>
                    <div className="sort-dir-btns-v2">
                      <button type="button" className={`sort-dir-btn-v2${pendingDir === "desc" ? " active" : ""}`} onClick={() => setPendingDir("desc")}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="3" y1="6" x2="15" y2="6"/><line x1="3" y1="12" x2="11" y2="12"/><line x1="3" y1="18" x2="7" y2="18"/>
                          <polyline points="18 9 21 12 18 15"/>
                        </svg>
                        Descending
                      </button>
                      <button type="button" className={`sort-dir-btn-v2${pendingDir === "asc" ? " active" : ""}`} onClick={() => setPendingDir("asc")}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="3" y1="6" x2="7" y2="6"/><line x1="3" y1="12" x2="11" y2="12"/><line x1="3" y1="18" x2="15" y2="18"/>
                          <polyline points="18 15 21 12 18 9"/>
                        </svg>
                        Ascending
                      </button>
                    </div>
                  </div>
                  <button type="button" className="sort-apply-btn-v2" onClick={() => { handleSortChange(pendingField, pendingDir); setShowSortDropdown(false); }}>
                    Apply sort
                  </button>
                </div>,
                document.body
              )}

              {/* Refresh */}
              <button className="header-action-btn-v2" onClick={() => triggerFetch()} disabled={isDisabled} title="Refresh">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
              </button>

              {/* Export dropdown */}
              <div ref={exportDropdownRef}>
                <button className="header-action-btn-v2" disabled={exportLoading || isDisabled} onClick={() => {
                  if (!showExportDropdown && exportDropdownRef.current)
                    setExportBtnPos(calcPos(exportDropdownRef.current, 4, 220));
                  setShowExportDropdown((s) => !s);
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {exportLoading ? "Exporting…" : "Export"}
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ marginLeft: 2 }}>
                    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              {showExportDropdown && exportBtnPos && createPortal(
                <div className="export-dropdown-v2" ref={exportDropdownPortalRef}
                  style={{ position: "fixed", top: exportBtnPos.top, left: exportBtnPos.left, zIndex: 9999 }}>
                  <button type="button" className="export-dd-item" onClick={() => handleExport("csv")}>
                    <span>CSV – all matches</span>
                    <small>Up to 10,000 rows</small>
                  </button>
                  <button type="button" className="export-dd-item" onClick={() => handleExport("xlsx")}>
                    <span>Excel (XLSX) – all matches</span>
                    <small>Up to 10,000 rows</small>
                  </button>
                </div>,
                document.body
              )}
            </div>
          </div>

          {/* Active filter chips – full width below header row */}
          {activeFilterChips.length > 0 && (
            <div className="active-filters-bar">
              {activeFilterChips.map((chip) => (
                <div key={chip.key} className="filter-chip">
                  <span className="filter-chip-cat">{chip.category}:</span>
                  <span>{chip.label}</span>
                  <button className="chip-remove-btn" onClick={() => activeFilterChips.length === 1 ? handleClearAllFilters() : removeChip(chip.key)} title="Remove filter">×</button>
                </div>
              ))}
              <button className="clear-filters-link" onClick={handleClearAllFilters}>Clear all</button>
            </div>
          )}
        </div>

        {/* Bulk action bar */}
        {selectedJobs.length > 0 && (
          <div className="bulk-action-bar-v2">
            <span>{selectedJobs.length} selected</span>
            <div className="bulk-actions">
              <button className="bulk-btn-v2" onClick={openApplyStageModal} disabled={isDisabled}>Assign Stage</button>
              <button className="bulk-btn-v2 reject" onClick={() => handleBulkStatusChange("Rejected")} disabled={isDisabled}>Reject</button>
              <button className="bulk-btn-v2 hold" onClick={() => handleBulkStatusChange("On Hold")} disabled={isDisabled}>On Hold</button>
              <button className="bulk-btn-v2 shortlist" onClick={() => handleBulkStatusChange("Short Listed")} disabled={isDisabled}>Shortlist</button>
              <button className="bulk-btn-v2 hire" onClick={() => handleBulkStatusChange("Hired")} disabled={isDisabled}>Mark Hired</button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="candidates-table-outer">
          <div className="table-scroll-body">
            <table className="candidates-table-v2">
              <thead>
                <tr>
                  <th className="th-check">
                    <input type="checkbox" onChange={handleSelectAll} checked={selectedJobs.length === candidateList.length && candidateList.length > 0} />
                  </th>
                  <th className="th-candidate">CANDIDATE</th>
                  <th className="th-added">DAYS ADDED</th>
                  <th className="th-resume">RESUME SCORE</th>
                  <th className="th-stages">
                    <div>Stage Scores</div>
                    <div className="stage-abbrev-row">
                      {JOB_STAGES.map((s, i) => (
                        <span key={s.name}>{s.abbrev}{i < JOB_STAGES.length - 1 && <span className="stage-dot"> · </span>}</span>
                      ))}
                    </div>
                  </th>
                  <th className="th-status">STATUS</th>
                  <th className="th-actions">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7">
                      <div className="table-loading">
                        <div className="spinner-border" role="status" style={{ color: "#F75891" }}>
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : candidateList.length > 0 ? (
                  currentJobs.map((job) => {
                    const candidate = candidateList.find((c) => c.id === job.id);
                    return (
                      <tr key={job.id} className={selectedJobs.includes(job.id) ? "row-selected" : ""}>
                        <td className="td-check">
                          <input type="checkbox" checked={selectedJobs.includes(job.id)} onChange={() => handleSelectJob(job.id)} />
                        </td>
                        <td className="td-candidate">
                          <strong className="candidate-name-link"
                            onClick={() => window.open(`/jobs/${jobId}/candidates/${job.id}?tab=all`, "_blank")}>
                            {job.candidates}
                          </strong>
                          {job.phone_number && <div className="candidate-phone-v2">{job.phone_number}</div>}
                        </td>
                        <td className="td-added">
                          <div style={{ fontSize: "13px", fontWeight: 500 }}>{getDaysFromToday(job.createdAt)}</div>
                          <div style={{ fontSize: "11px", color: "#8A9199", marginTop: 2 }}>{job.addedOn}</div>
                        </td>
                        <td className="td-resume"><ScoreCircle score={job.resumeScore} /></td>
                        <td className="td-stages">
                          <div className="stage-scores-grid">
                            {JOB_STAGES.map((stage) => {
                              const data = getCandidateStageScore(candidate, stage.id);
                              return (
                                <div key={stage.name} className="stage-score-cell">
                                  <span className="stage-cell-abbrev" title={stage.name}>{stage.abbrev}</span>
                                  {data ? (
                                    data.score != null ? (
                                      <span className="stage-cell-score">{Math.round(data.score)}%</span>
                                    ) : data.status && data.status !== "Not Started" ? (
                                      renderStageStatusIcon(data.status)
                                    ) : (
                                      <span className="stage-cell-dash">—</span>
                                    )
                                  ) : (
                                    <span className="stage-cell-dash">—</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                        <td className="td-status">
                          {statusLoading[job.id] ? (
                            <div className="spinner-border" style={{ width: "1rem", height: "1rem", color: "#F75891" }} />
                          ) : (() => {
                            const hiringStatusName = candidateList.find((c) => c.id === job.id)?.hiring_status?.name || "In Progress";
                            const cfg = getStatusVisual(hiringStatusName);
                            const isOpen = openStatusDropdown === job.id;
                            const selectableStatuses = hiringStatuses.filter((s) => s.name !== hiringStatusName);
                            return (
                              <div className="status-dropdown-v2">
                                <button
                                  className="status-dd-btn"
                                  style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}
                                  onClick={() => setOpenStatusDropdown(isOpen ? null : job.id)}
                                  disabled={isDisabled}
                                >
                                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.color, flexShrink: 0, display: "inline-block" }} />
                                  {cfg.label}
                                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ marginLeft: 2 }}>
                                    <path d="M2 3.5L5 6.5L8 3.5" stroke={cfg.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </button>
                                {isOpen && (
                                  <div className="status-dd-menu">
                                    {selectableStatuses.length === 0 ? (
                                      <div className="status-dd-empty">No other statuses available</div>
                                    ) : (
                                      selectableStatuses.map((s) => {
                                        const optCfg = getStatusVisual(s.name);
                                        return (
                                          <button
                                            key={s.id}
                                            className="status-dd-item"
                                            onClick={() => {
                                              handleStatusChange(job.id, s);
                                              setOpenStatusDropdown(null);
                                            }}
                                          >
                                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: optCfg.color, flexShrink: 0 }} />
                                            <span>{optCfg.label}</span>
                                          </button>
                                        );
                                      })
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="td-actions">
                          <div className="action-btns-v2">
                            {/* View icon */}
                            <button
                              className="action-btn-v2 view-btn"
                              title="View candidate"
                              disabled={isDisabled}
                              onClick={() => window.open(`/jobs/${jobId}/candidates/${job.id}?tab=all`, "_blank")}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                            {/* Edit icon */}
                            <button
                              className="action-btn-v2 edit-btn"
                              title="Edit candidate"
                              disabled={isDisabled}
                              onClick={() => {
                                setEditModal({ open: true, candidate });
                                setEditForm({
                                  name: candidate?.name || "",
                                  email: candidate?.email || "",
                                  phone_number: candidate?.phone_number || "",
                                });
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="no-data-cell">No candidates found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && (
            <div className="pagination-v2">
              <span className="pagination-info-v2">
                {pagination.total > 0 ? (
                  <>Showing{" "}
                    <strong>{(pagination.page - 1) * pagination.page_size + 1}{" "}–{" "}{Math.min(pagination.page * pagination.page_size, pagination.total)}</strong>{" "}
                    of {pagination.total} candidates</>
                ) : "No results"}
              </span>
              <div className="pagination-controls-v2">{pagination.total > 0 && renderPagination()}</div>
              <div className="page-size-select-v2">
                <span>Per page</span>
                <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Hidden file inputs */}
      <input ref={csvInputRef} type="file" accept=".csv,.xls,.xlsx" style={{ display: "none" }} onChange={handleCsvUpload} />
      <input ref={pdfInputRef} type="file" accept=".pdf,.docx" multiple style={{ display: "none" }} onChange={handlePdfUpload} />
      <input ref={resumeInputRef} type="file" accept=".pdf,.docx" style={{ display: "none" }} />

      {/* Upload modal */}
      {showUploadModal && (
        <UploadCandidateModal show={showUploadModal} uploading={uploading} file={file} uploadProgress={uploadProgress}
          csvInputRef={csvInputRef} pdfInputRef={pdfInputRef}
          handleCsvClick={() => csvInputRef.current.click()} handlePdfClick={() => pdfInputRef.current.click()}
          handleCsvUpload={handleCsvUpload} handlePdfUpload={handlePdfUpload}
          handleCandidateUpload={handleCandidateUpload} resetUploadModal={resetUploadModal}
          downloadSampleCsv={downloadSampleCsv} />
      )}

      {/* Resume upload modal */}
      <ResumeUploadModal show={resumeModal.open} uploading={uploading} file={file} resumeInputRef={resumeInputRef}
        onClose={() => setResumeModal({ open: false, candidateId: null })}
        onFileSelect={(e) => {
          const f = e.target.files[0];
          if (!f || !["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(f.type)) {
            showSnack("Only PDF and DOCX files are supported."); return;
          }
          setFile(f);
        }}
        onUploadClick={handleResumeUpload} />

      {/* Edit candidate modal */}
      {editModal.open && (
        <div className="jv2-modal-overlay">
          <div className="jv2-modal" style={{ maxWidth: 500 }}>
            <div className="jv2-modal-head">
              <h3>Edit Candidate</h3>
              <button className="jv2-modal-close" onClick={() => setEditModal({ open: false, candidate: null })}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="jv2-modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div className="modal-input-label">
                <label>Name :</label>
                <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div className="modal-input-label">
                <label>Email :</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div className="modal-input-label">
                <label>Phone Number :</label>
                <input type="text" value={editForm.phone_number} onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })} />
              </div>
            </div>
            <div className="jv2-modal-footer">
              <button className="jv2-modal-cancel" onClick={() => setEditModal({ open: false, candidate: null })}>Cancel</button>
              <button className="jv2-modal-confirm" disabled={isDisabled} onClick={handleEditCandidate}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign stage modal */}
      {isOpen && (
        <ApplyStageModal isOpen={isOpen} setIsOpen={() => setIsOpen(false)}
          selectAssignedStage={selectAssignedStage} jobId={currentJobs[0]?.job_id}
          loading={loading} assignProgress={assignProgress} />
      )}

      {/* Save search modal */}
      {saveSearchModal.open && (
        <div className="jv2-modal-overlay" onClick={() => setSaveSearchModal({ open: false, name: "" })}>
          <div className="jv2-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="jv2-modal-head">
              <h3>Save current search</h3>
              <button className="jv2-modal-close" onClick={() => setSaveSearchModal({ open: false, name: "" })}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="jv2-modal-body">
              <label className="save-search-field-label">Advanced search name</label>
              <input type="text" className="save-search-name-input" placeholder="e.g. Shortlisted in Mumbai"
                value={saveSearchModal.name}
                onChange={(e) => setSaveSearchModal((prev) => ({ ...prev, name: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") handleConfirmSaveSearch(); }} />
              {activeFilterChips.length > 0 && (
                <p style={{ textAlign: "left", fontSize: 12, color: "#8a9199", marginTop: 6, marginBottom: 0 }}>
                  {activeFilterChips.length} filter{activeFilterChips.length > 1 ? "s" : ""} will be saved.
                </p>
              )}
            </div>
            <div className="jv2-modal-footer">
              <button className="jv2-modal-cancel" onClick={() => setSaveSearchModal({ open: false, name: "" })}>Cancel</button>
              <button className="jv2-modal-confirm" onClick={handleConfirmSaveSearch}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Saved searches modal */}
      {savedSearchesModal.open && (
        <div className="jv2-modal-overlay" onClick={() => setSavedSearchesModal((prev) => ({ ...prev, open: false }))}>
          <div className="jv2-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="jv2-modal-head">
              <h3>Saved searches</h3>
              <button className="jv2-modal-close" onClick={() => setSavedSearchesModal((prev) => ({ ...prev, open: false }))}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="jv2-modal-body" style={{ maxHeight: 380, overflowY: "auto", scrollbarWidth: "none" }}>
              {savedSearchesModal.listLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
                  <div className="spinner-border" style={{ color: "#F75891" }} />
                </div>
              ) : savedSearchesModal.list.length === 0 ? (
                <p style={{ textAlign: "center", color: "#8A9199", fontSize: 13, margin: 0 }}>No saved searches for this job yet.</p>
              ) : (
                savedSearchesModal.list.map((item) => (
                  <div key={item.id} className="saved-search-item-v2">
                    <div className="saved-search-item-info">
                      <strong>{item.name}</strong>
                      <span>{item.summary}</span>
                      <span className="saved-search-date">{new Date(item.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="saved-search-item-actions">
                      <button type="button" className="saved-search-load-btn" onClick={() => handleLoadSavedSearch(item)}>Load</button>
                      <button type="button" className="saved-search-del-btn" onClick={() => handleDeleteSavedSearch(item.id)}>Delete</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <Snackbar anchorOrigin={{ vertical: "top", horizontal: "center" }} open={snackOpen} autoHideDuration={3000} onClose={snackClosed}>
        <Alert onClose={snackClosed} severity={snackSeverity} sx={{ width: "100%" }}>{snackMessage}</Alert>
      </Snackbar>
    </div>
  );
};

export default CandidatesV2;
